import React, { useState, useEffect, useRef } from "react";
import { Modal, Button, Table, message, Space, Input } from "antd";
import { DeleteOutlined } from "@ant-design/icons";
import { LineConfigAPI, NotifyGroupAPI } from "../../constants/GenericAPI";

export const PickLineSettingModal = ({ visible, onCancel, onPickup, selectedGroup, selectedLineSetting, selectedIds = [], multiSelect = false, pickupFormat = 'ids' }) => {
  const [lineSettings, setLineSettings] = useState([]);
  const [filteredSettings, setFilteredSettings] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [loading, setLoading] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  // 添加一個 ref 來追蹤組件是否已卸載
  const isMounted = useRef(true);
  // 添加一個 ref 來追蹤 Modal 是否可見
  const isVisibleRef = useRef(visible);

  // 在組件卸載時設置 isMounted 為 false
  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  // 更新 isVisibleRef 並處理 modal 關閉時的清理工作
  useEffect(() => {
    isVisibleRef.current = visible;
    if (!visible) {
      setSearchText("");
      setFilteredSettings(lineSettings);
    }
  }, [visible, lineSettings]);

  // 獲取所有 LINE 設定
  const fetchLineSettings = async () => {
    setLoading(true);
    try {
      const response = await LineConfigAPI.getAll();
      if (isMounted.current) {
        const data = response.data.data || [];
        setLineSettings(data);
        setFilteredSettings(data);
      }
    } catch (error) {
      console.error("獲取 LINE 設定失敗:", error);
      console.error("錯誤詳情:", {
        message: error.message,
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        url: error.config?.url
      });
      if (isMounted.current) {
        message.error(`獲取 LINE 設定失敗: ${error.response?.status || '網路錯誤'}`);
      }
    } finally {
      if (isMounted.current) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    if (visible) {
      console.log("selectedIds:", selectedIds);
      console.log("selectedLineSetting:", selectedLineSetting);
      fetchLineSettings();
      if (selectedIds && selectedIds.length > 0) {
        setSelectedRowKeys(selectedIds);
      } else if (selectedLineSetting) {
        setSelectedRowKeys([selectedLineSetting.id]);
      } else {
        setSelectedRowKeys([]);
      }
      // 重置搜尋
      setSearchText("");
    }
  }, [visible, selectedLineSetting, selectedIds]);

  // 處理搜尋
  useEffect(() => {
    if (searchText === "") {
      setFilteredSettings(lineSettings);
      return;
    }
    const searchLower = searchText.toLowerCase();
    const filtered = lineSettings.filter(item => 
      (item.setting_name?.toLowerCase().includes(searchLower)) ||
      (item.chat_id?.toString().toLowerCase().includes(searchLower))
    );
    setFilteredSettings(filtered);
  }, [searchText, lineSettings]);

  // 處理選擇行的變更
  const handleRowSelectionChange = (selectedKeys) => {
    setSelectedRowKeys(selectedKeys);
  };

  // 處理點擊行的事件
  const handleRowClick = (record) => {
    if (multiSelect) {
      const newKeys = selectedRowKeys.includes(record.id)
        ? selectedRowKeys.filter(key => key !== record.id)
        : [...selectedRowKeys, record.id];
      setSelectedRowKeys(newKeys);
    } else {
      setSelectedRowKeys([record.id]);
    }
  };

  // 處理選擇設定的確認
  const handlePickSetting = async () => {
    if (selectedRowKeys.length === 0) {
      message.warning("請先選擇至少一個 LINE 設定");
      return;
    }

    if (pickupFormat === 'object') {
      // 返回設定對象
      const selectedSetting = lineSettings.find((setting) => setting.id === selectedRowKeys[0]);
      if (selectedSetting) {
        try {
          // 直接創建只包含選中設定 ID 的數組
          const lineIds = [selectedSetting.id];

          // 更新 selectedGroup 的 line_ids，只保存一個 ID
          const updatedGroup = {
            ...selectedGroup,
            line_ids: JSON.stringify(lineIds),
          };

          // 更新群組設定
          await NotifyGroupAPI.update(selectedGroup.id, updatedGroup);

          onPickup(selectedSetting);

          // 關閉 Modal
          onCancel();
        } catch (error) {
          console.error("選擇 LINE 設定失敗:", error);
          if (isMounted.current && isVisibleRef.current) {
            message.error("選擇 LINE 設定失敗");
          }
        }
      }
    } else {
      // 返回 ID 數組 (默認行為)
      console.log('確認選擇的 LINE 設定 IDs:', selectedRowKeys);
      onPickup(selectedRowKeys);

      // 關閉 Modal
      onCancel();
    }
  };

  // 處理刪除單個已選擇的 LINE 設定 (需要後端更新)
  const handleRemoveLineSettingForSingle = async () => {
    try {
      // 更新 selectedGroup 的 line_ids，設為空數組
      const updatedGroup = {
        ...selectedGroup,
        line_ids: JSON.stringify([]),
      };

      // 更新群組設定
      await NotifyGroupAPI.update(selectedGroup.id, updatedGroup);

      // 通知父組件已刪除設定
      onPickup(null);

      // 顯示成功訊息
      message.success("已移除 LINE 設定");

      // 關閉 Modal
      onCancel();
    } catch (error) {
      console.error("移除 LINE 設定失敗:", error);
      if (isMounted.current && isVisibleRef.current) {
        message.error("移除 LINE 設定失敗");
      }
    }
  };

  // 處理刪除多個已選擇的 LINE 設定 (僅前端更新)
  const handleRemoveLineSettingForMultiple = () => {
    // 僅通知父組件清除前端顯示，不發送後端請求
    console.log('清除前端 LINE 設定顯示，selectedIds:', selectedIds);
    onPickup(null);
    message.success("已清除 LINE 設定顯示");
    onCancel();
  };

  // 定義表格列
  const columns = [
    {
      title: "ID",
      dataIndex: "id",
      align: "center",
      key: "id",
      width: 60,
    },
    {
      title: "群組名稱",
      dataIndex: "setting_name",
      key: "setting_name",
    },
    {
      title: "客戶群組",
      dataIndex: "chat_id",
      key: "chat_id",
      ellipsis: true,
      sorter: (a, b) => {
        // 如果 chat_id 是字串，使用字串比較
        if (typeof a.chat_id === "string" && typeof b.chat_id === "string") {
          return a.chat_id.localeCompare(b.chat_id);
        }
        // 如果是數字，直接比較
        return a.chat_id - b.chat_id;
      },
      sortDirections: ["ascend", "descend"],
    },
    {
      title: "訊息方案",
      dataIndex: "plan_messages",
      key: "plan_messages",
      render: (plan_messages) => {
        const plans = ["常規用量(0-100則)", "增額用量(101-300則)", "高額用量(301-500則)"];
        return plans[plan_messages] || "未知方案";
      }
    },
    {
      title: "狀態",
      dataIndex: "is_active",
      key: "is_active",
      align: "center",
      width: 80,
      render: (is_active) => <span style={{ color: is_active === 1 ? "#52c41a" : "#ff4d4f" }}>{is_active === 1 ? "啟用" : "停用"}</span>,
    }
  ];

  // 確定 Modal 的 footer 按鈕
  const modalFooter = [
    <Button key="cancel" onClick={onCancel}>
      取消
    </Button>
  ];

  // 如果有 selectedLineSetting，添加刪除按鈕 (需要後端更新)
  if (selectedLineSetting) {
    modalFooter.unshift(
      <Button key="remove-single" danger onClick={handleRemoveLineSettingForSingle} icon={<DeleteOutlined />}>
        移除設定
      </Button>
    );
  }

  // 如果有 selectedIds，添加清除按鈕 (僅前端更新)
  if (selectedIds && selectedIds.length > 0) {
    modalFooter.unshift(
      <Button key="remove-multiple" danger onClick={handleRemoveLineSettingForMultiple} icon={<DeleteOutlined />}>
        清除設定顯示
      </Button>
    );
  }

  // 添加確認選擇按鈕
  modalFooter.push(
    <Button key="pick" type="default" style={{ background: "var(--content-background)", color: "var(--primary-text)", border: "2px solid var(--primary-border)" }} onClick={handlePickSetting} disabled={selectedRowKeys.length === 0}>
      確認選擇
    </Button>
  );

  return (
    <Modal
      title={`選擇 LINE 設定 - ${selectedGroup?.group_name}`}
      open={visible}
      onCancel={onCancel}
      width={900}
      footer={modalFooter}
    >
      <div style={{ marginBottom: 16 }}>
        <Space direction="vertical" style={{ width: '100%' }}>
          {selectedLineSetting && (
            <Space>
              <span>目前已選擇:</span>
              <span style={{ fontWeight: 'bold' }}>{selectedLineSetting.setting_name}</span>
              <span>(ID: {selectedLineSetting.id})</span>
            </Space>
          )}
          {selectedIds && selectedIds.length > 0 && !selectedLineSetting && (
            <Space direction="vertical" size="small">
              <span>目前已選擇:</span>
              {selectedIds.map(id => {
                const setting = lineSettings.find(s => s.id === id);
                return setting ? (
                  <span key={id} style={{ fontWeight: 'bold' }}>
                    {id} - {setting.setting_name}
                  </span>
                ) : (
                  <span key={id} style={{ fontWeight: 'bold' }}>
                    ID: {id}
                  </span>
                );
              })}
            </Space>
          )}
          <Input
            className="search-input"
            placeholder="搜尋群組名稱或客戶群組"
            value={searchText}
            onChange={e => setSearchText(e.target.value)}
            style={{ width: 300 }}
          />
        </Space>
      </div>
      <Table
        columns={columns}
        dataSource={filteredSettings}
        rowKey="id"
        loading={loading}
        rowSelection={{
          type: multiSelect ? "checkbox" : "radio",
          selectedRowKeys,
          onChange: handleRowSelectionChange,
        }}
        onRow={(record) => ({
          onClick: () => handleRowClick(record), // 點擊行時選中該行
          style: { cursor: 'pointer' } // 改變滑鼠指針樣式，提示可點擊
        })}
        pagination={{
          defaultPageSize: 10,
          showTotal: (total) => `共 ${total} 筆資料`,
        }}
      />
    </Modal>
  );
};